import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Thermometer,
  Droplets,
  Cable,
  Layers,
  Wifi,
  WifiOff,
  Sun,
  Moon,
  Cpu,
  Sprout,
  Factory,
  Wrench,
  Mail,
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  Send,
  Loader2,
  Activity,
  Droplet,
  LogIn,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { api, formatApiError } from "../lib/api";
import { useTheme } from "../lib/theme";
import { toast } from "sonner";

const CONTACT_EMAIL = "info@olimpiasensor.com";

export default function Landing() {
  return (
    <div
      className="min-h-screen bg-[#F4F6F8] dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors"
      data-testid="landing-page"
    >
      <Nav />
      <Hero />
      <MeasureGrid />
      <CustomSolutions />
      <ConnectivitySplit />
      <Visualization />
      <Applications />
      <ContactSection />
      <Footer />
    </div>
  );
}

/* ----------------------- NAV ----------------------- */
function Nav() {
  const { theme, toggle } = useTheme();
  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-md bg-white/80 dark:bg-slate-950/75 border-b border-slate-200/60 dark:border-slate-800/80"
      data-testid="landing-nav"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3 group" aria-label="Olimpia Sensor">
          <img
            src="/brand/logo.png"
            alt=""
            className="h-9 w-9 object-contain dark:[filter:brightness(0)_invert(1)]"
            draggable="false"
          />
          <div className="leading-tight">
            <div className="text-[11px] tracking-[0.28em] uppercase text-slate-500 dark:text-slate-400 font-medium">
              Olimpia
            </div>
            <div className="text-[15px] font-semibold tracking-tight text-slate-900 dark:text-slate-50 -mt-0.5">
              Sensor
            </div>
          </div>
        </a>
        <nav className="hidden md:flex items-center gap-7 text-sm text-slate-600 dark:text-slate-300">
          <a href="#medir" className="hover:text-slate-900 dark:hover:text-white transition-colors">
            Qué medimos
          </a>
          <a href="#a-medida" className="hover:text-slate-900 dark:hover:text-white transition-colors">
            A medida
          </a>
          <a href="#aplicaciones" className="hover:text-slate-900 dark:hover:text-white transition-colors">
            Aplicaciones
          </a>
          <a href="#contacto" className="hover:text-slate-900 dark:hover:text-white transition-colors">
            Contacto
          </a>
        </nav>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={toggle}
            aria-label="Cambiar tema"
            data-testid="landing-theme-toggle"
            className="h-9 w-9 grid place-items-center rounded-full text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <Link
            to="/login"
            className="hidden sm:inline-block text-xs text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors px-2"
            data-testid="landing-client-access"
          >
            Acceso clientes
          </Link>
          <Link
            to="/login"
            className="sm:hidden inline-flex items-center gap-1.5 rounded-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 h-9 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors whitespace-nowrap"
            data-testid="landing-client-access-mobile"
            aria-label="Acceso clientes / Log in"
          >
            <LogIn className="h-3.5 w-3.5" />
            Log in
          </Link>
          <a href="#contacto">
            <Button
              size="sm"
              className="rounded-full bg-[#0A55C2] hover:bg-[#0848a8] text-white shadow-sm shadow-[#0A55C2]/20"
              data-testid="landing-nav-cta"
            >
              <span className="hidden sm:inline">Cuéntanos tu caso</span>
              <span className="sm:hidden">Contactar</span>
              <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
            </Button>
          </a>
        </div>
      </div>
    </header>
  );
}

/* ----------------------- HERO ----------------------- */
function Hero() {
  return (
    <section
      id="top"
      className="relative overflow-hidden border-b border-slate-200/60 dark:border-slate-800/80"
    >
      {/* Subtle grid (works in both themes) */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.35] dark:opacity-[0.18]"
        style={{
          backgroundImage:
            "linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)",
          color: "rgba(15,23,42,0.06)",
          backgroundSize: "44px 44px",
          maskImage: "radial-gradient(ellipse at center, black 40%, transparent 78%)",
        }}
      />
      {/* Soft accent glow only in dark mode for ambience (no halo around device) */}
      <div
        aria-hidden
        className="hidden dark:block absolute inset-0 opacity-60"
        style={{
          backgroundImage:
            "radial-gradient(900px 500px at 90% -10%, rgba(10,85,194,0.18), transparent 60%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-300/60 dark:border-slate-700 bg-white/70 dark:bg-slate-900/70 px-3 py-1 text-[11px] tracking-[0.18em] uppercase text-slate-600 dark:text-slate-300 mb-7">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Sensórica a medida · Ingeniería aplicada
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-slate-900 dark:text-white">
              Soluciones <span className="text-[#0A55C2] dark:text-[#5B9BFF]">a medida</span> para medir
              <br className="hidden sm:block" />
              lo que necesitas.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 dark:text-slate-300 leading-relaxed max-w-2xl">
              Diseñamos y desarrollamos sistemas de medición y monitorización adaptados a cada caso:
              temperatura, humedad del suelo, sondas, varias variables a la vez. Con acceso web a los datos
              cuando hace falta, o autónomos sin conexión a internet.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a href="#contacto">
                <Button
                  size="lg"
                  className="rounded-full bg-[#0A55C2] hover:bg-[#0848a8] text-white px-7 h-12 shadow-md shadow-[#0A55C2]/25"
                  data-testid="landing-hero-primary-cta"
                >
                  Cuéntanos tu caso
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </a>
              <a href="#aplicaciones">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-7 h-12 border-slate-300 dark:border-slate-700 hover:bg-white dark:hover:bg-slate-900"
                  data-testid="landing-hero-secondary-cta"
                >
                  Ver aplicaciones
                </Button>
              </a>
            </div>

            <dl className="mt-10 grid grid-cols-3 gap-6 max-w-xl">
              <div>
                <dt className="text-[11px] tracking-[0.18em] uppercase text-slate-500 dark:text-slate-400">
                  Diseño
                </dt>
                <dd className="text-sm font-semibold mt-1 text-slate-900 dark:text-white">A medida</dd>
              </div>
              <div>
                <dt className="text-[11px] tracking-[0.18em] uppercase text-slate-500 dark:text-slate-400">
                  Conectividad
                </dt>
                <dd className="text-sm font-semibold mt-1 text-slate-900 dark:text-white">Online u offline</dd>
              </div>
              <div>
                <dt className="text-[11px] tracking-[0.18em] uppercase text-slate-500 dark:text-slate-400">
                  Alimentación
                </dt>
                <dd className="text-sm font-semibold mt-1 text-slate-900 dark:text-white">Red, batería, solar</dd>
              </div>
            </dl>
          </div>

          {/* Packshot — clean, no floating data cards, no halo */}
          <div className="lg:col-span-5 relative">
            <img
              src="/brand/olimpia-device.png"
              alt="Dispositivo Olimpia Sensor"
              className="w-full max-w-md mx-auto select-none"
              draggable="false"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ----------------------- WHAT WE MEASURE ----------------------- */
function MeasureGrid() {
  const items = [
    { icon: Thermometer, title: "Temperatura ambiente", desc: "Lecturas precisas de aire en interiores y exteriores." },
    { icon: Droplets, title: "Humedad del suelo", desc: "Medición agronómica continua para riego y cultivo." },
    { icon: Cable, title: "Temperatura con sonda", desc: "Sondas en líquidos, conductos, equipos o procesos." },
    { icon: Layers, title: "Varias temperaturas a la vez", desc: "Múltiples puntos en una sola caja de medición." },
    { icon: Activity, title: "Otras variables", desc: "Presión, nivel, conductividad, CO₂… consúltanos tu caso." },
    { icon: ShieldCheck, title: "Tu necesidad concreta", desc: "Si no aparece arriba, lo más probable es que podamos hacerlo." },
  ];
  return (
    <section
      id="medir"
      className="py-20 sm:py-24 border-b border-slate-200/60 dark:border-slate-800/80"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeader
          eyebrow="Qué podemos medir"
          title="Variables reales, soluciones reales."
          subtitle="Estos son ejemplos habituales. El sistema se adapta al caso del cliente: tipo de variable, condiciones de instalación y nivel de precisión."
        />
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((it) => (
            <div
              key={it.title}
              className="group relative rounded-2xl bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 p-6 hover:border-[#0A55C2]/40 dark:hover:border-[#5B9BFF]/40 hover:shadow-lg hover:shadow-[#0A55C2]/5 transition-all"
              data-testid={`measure-card-${it.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <div className="h-10 w-10 rounded-lg bg-slate-100 dark:bg-slate-800 group-hover:bg-[#0A55C2]/10 dark:group-hover:bg-[#0A55C2]/20 grid place-items-center transition-colors">
                <it.icon className="h-4 w-4 text-slate-700 dark:text-slate-300 group-hover:text-[#0A55C2] dark:group-hover:text-[#5B9BFF] transition-colors" />
              </div>
              <h3 className="mt-5 font-semibold tracking-tight text-slate-900 dark:text-white">{it.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{it.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ----------------------- CUSTOM SOLUTIONS ----------------------- */
function CustomSolutions() {
  const rows = [
    { label: "Tipo de sensor o sonda", value: "Adaptado al fenómeno físico que se quiere medir." },
    { label: "Número de variables", value: "Una o varias entradas en un mismo equipo." },
    { label: "Conectividad", value: "WiFi, móvil (4G/LTE), LoRa o totalmente offline." },
    { label: "Alimentación", value: "Red eléctrica, baterías o panel solar autónomo." },
    { label: "Acceso a los datos", value: "Lectura local en el equipo o consulta remota vía web." },
  ];
  return (
    <section
      id="a-medida"
      className="py-20 sm:py-24 bg-white dark:bg-slate-900/30 border-b border-slate-200/60 dark:border-slate-800/80"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-24">
          <SectionHeader
            eyebrow="Soluciones a medida"
            title="No vendemos un producto cerrado. Construimos el que necesitas."
            subtitle="Analizamos el caso, diseñamos el dispositivo y elegimos contigo cada decisión técnica."
            align="left"
          />
          <a href="#contacto" className="inline-block mt-6">
            <Button
              variant="outline"
              className="rounded-full border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
              data-testid="custom-cta"
            >
              Consultar mi caso
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </a>
        </div>
        <div className="lg:col-span-7">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden divide-y divide-slate-200/70 dark:divide-slate-800 bg-white dark:bg-slate-900/40">
            {rows.map((r, idx) => (
              <div
                key={r.label}
                className="grid grid-cols-12 gap-4 p-5 sm:p-6 hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition-colors"
              >
                <div className="col-span-12 sm:col-span-4 flex items-center gap-3">
                  <div className="h-7 w-7 rounded-md bg-[#0A55C2]/10 dark:bg-[#0A55C2]/20 text-[#0A55C2] dark:text-[#5B9BFF] grid place-items-center text-[11px] font-mono tabular-nums">
                    0{idx + 1}
                  </div>
                  <span className="text-sm font-semibold tracking-tight text-slate-900 dark:text-white">
                    {r.label}
                  </span>
                </div>
                <div className="col-span-12 sm:col-span-8 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  {r.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ----------------------- CONNECTIVITY SPLIT ----------------------- */
function ConnectivitySplit() {
  return (
    <section className="py-20 sm:py-24 border-b border-slate-200/60 dark:border-slate-800/80">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeader
          eyebrow="Con o sin internet"
          title="Flexibilidad, no limitación."
          subtitle="Adaptamos la solución al entorno donde tiene que funcionar. Si hay cobertura, los datos llegan a la web. Si no la hay, el equipo sigue trabajando solo."
        />
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          <div className="relative rounded-2xl bg-[#0A55C2] text-white p-8 sm:p-10 overflow-hidden shadow-xl shadow-[#0A55C2]/20">
            <div
              aria-hidden
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage:
                  "radial-gradient(700px 300px at 90% -10%, rgba(255,255,255,0.5), transparent 60%)",
              }}
            />
            <div className="relative">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[11px] tracking-[0.18em] uppercase">
                <Wifi className="h-3 w-3" /> Conectado
              </div>
              <h3 className="mt-5 text-2xl font-bold tracking-tight leading-tight">
                Acceso a los datos vía web
              </h3>
              <p className="mt-3 text-white/85 text-sm leading-relaxed">
                Consulta remota desde cualquier dispositivo. Gráficas, lecturas recientes y alertas
                cuando un valor sale del rango definido.
              </p>
              <ul className="mt-6 space-y-2.5 text-sm">
                {["Seguimiento en tiempo casi real", "Histórico consultable", "Alertas y avisos"].map((s) => (
                  <li key={s} className="flex items-start gap-2.5">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                    <span className="text-white/90">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="relative rounded-2xl bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 p-8 sm:p-10 overflow-hidden">
            <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 text-[11px] tracking-[0.18em] uppercase text-slate-700 dark:text-slate-300">
              <WifiOff className="h-3 w-3" /> Autónomo
            </div>
            <h3 className="mt-5 text-2xl font-bold tracking-tight leading-tight text-slate-900 dark:text-white">
              Funciona sin conexión a internet
            </h3>
            <p className="mt-3 text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
              Para ubicaciones aisladas o necesidades cerradas: el equipo mide, registra y opera de
              forma independiente sin depender de red.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm">
              {[
                "Sin depender de cobertura",
                "Lectura local en pantalla",
                "Alimentación por panel solar opcional",
              ].map((s) => (
                <li key={s} className="flex items-start gap-2.5 text-slate-700 dark:text-slate-300">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-[#0A55C2] dark:text-[#5B9BFF]" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 inline-flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <Sun className="h-3.5 w-3.5" /> Compatible con instalación solar
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ----------------------- VISUALIZATION ----------------------- */
function Visualization() {
  return (
    <section className="py-20 sm:py-24 bg-white dark:bg-slate-900/30 border-b border-slate-200/60 dark:border-slate-800/80">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <SectionHeader
            eyebrow="Visualización de datos"
            title="Tus lecturas, accesibles cuando importan."
            subtitle="Cuando la solución va conectada, los datos pueden consultarse en una interfaz limpia: gráficas, estado actual y los últimos valores. Forma parte del servicio, no es un software aparte."
            align="left"
          />
          <ul className="mt-7 space-y-3 text-sm text-slate-700 dark:text-slate-300">
            {[
              "Gráficas históricas por variable",
              "Lecturas recientes y estado del equipo",
              "Avisos cuando un valor sale del rango",
              "Acceso seguro mediante usuario y contraseña",
            ].map((s) => (
              <li key={s} className="flex items-start gap-2.5">
                <CheckCircle2 className="h-4 w-4 mt-0.5 text-[#0A55C2] dark:text-[#5B9BFF] shrink-0" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-7">
          <DashboardMockup />
        </div>
      </div>
    </section>
  );
}

function DashboardMockup() {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950 shadow-xl overflow-hidden">
      <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 flex items-center gap-2">
        <span className="h-2.5 w-2.5 rounded-full bg-rose-400/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
        <div className="ml-3 text-[11px] text-slate-400 dark:text-slate-500 font-mono">
          olimpiasensor.com / panel
        </div>
      </div>
      <div className="grid grid-cols-12 gap-3 p-4">
        <KpiTile
          label="Temperatura"
          value="23.4"
          unit="°C"
          color="#0A55C2"
          icon={Thermometer}
          spark={[12, 14, 13, 16, 18, 22, 21, 24, 23, 25, 23, 23.4]}
        />
        <KpiTile
          label="Tanque agua"
          value="56.1"
          unit="°C"
          color="#0E9F6E"
          icon={Droplet}
          spark={[55, 56, 54, 58, 56, 55, 56, 57, 56, 55, 56, 56.1]}
        />
        <KpiTile
          label="Sonda 1"
          value="78.2"
          unit="°C"
          color="#0A55C2"
          icon={Cable}
          spark={[70, 71, 72, 73, 74, 76, 78, 77, 79, 78, 78, 78.2]}
        />
        <div className="col-span-12 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-slate-400 dark:text-slate-500">
                Histórico 24h
              </div>
              <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Temperatura ambiente
              </div>
            </div>
            <span className="text-[11px] text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> en línea
            </span>
          </div>
          <MockChart />
        </div>
      </div>
    </div>
  );
}

function KpiTile({ label, value, unit, color, icon: Icon, spark }) {
  const min = Math.min(...spark);
  const max = Math.max(...spark);
  const path = spark
    .map((v, i) => {
      const x = (i / (spark.length - 1)) * 100;
      const y = 30 - ((v - min) / (max - min || 1)) * 28;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <div className="col-span-12 sm:col-span-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase text-slate-500 dark:text-slate-400">
          <Icon className="h-3.5 w-3.5" style={{ color }} />
          {label}
        </div>
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      </div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="text-2xl font-bold tabular-nums text-slate-900 dark:text-white">{value}</span>
        <span className="text-xs text-slate-500 dark:text-slate-400">{unit}</span>
      </div>
      <svg viewBox="0 0 100 30" className="w-full h-8 mt-2">
        <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    </div>
  );
}

function MockChart() {
  const data = [13, 12, 13, 15, 17, 19, 21, 23, 24, 24, 22, 21, 22, 23, 25, 26, 25, 24, 23, 22, 21, 22, 23, 23.4];
  const min = Math.min(...data);
  const max = Math.max(...data);
  const path = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * 100;
      const y = 60 - ((v - min) / (max - min || 1)) * 50;
      return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
  const area = `${path} L100,60 L0,60 Z`;
  return (
    <svg viewBox="0 0 100 60" className="w-full h-32" preserveAspectRatio="none">
      <defs>
        <linearGradient id="vizGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0A55C2" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#0A55C2" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#vizGrad)" />
      <path d={path} fill="none" stroke="#0A55C2" strokeWidth="0.8" strokeLinecap="round" />
    </svg>
  );
}

/* ----------------------- APPLICATIONS ----------------------- */
function Applications() {
  const apps = [
    {
      icon: Sprout,
      title: "Agricultura y riego",
      desc: "Humedad del suelo y temperatura para cultivos al aire libre.",
    },
    {
      icon: ShieldCheck,
      title: "Cumple con la legislación de la legionella",
      desc:
        "Monitoriza la temperatura de tanques de agua con envío de alertas. Más fiable, más económico.",
    },
    {
      icon: Factory,
      title: "Instalaciones técnicas",
      desc: "Procesos industriales con sondas en puntos críticos.",
    },
    {
      icon: Wind,
      title: "Control ambiental",
      desc: "Edificios, almacenes y zonas con requisitos térmicos.",
    },
    {
      icon: Wrench,
      title: "Proyectos especiales",
      desc: "Mediciones poco habituales o específicas de un cliente.",
    },
    {
      icon: Cpu,
      title: "Tu caso",
      desc: "Si no encaja en estos ejemplos, consúltanos igualmente.",
    },
  ];
  return (
    <section
      id="aplicaciones"
      className="py-20 sm:py-24 border-b border-slate-200/60 dark:border-slate-800/80"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeader
          eyebrow="Aplicaciones"
          title="Dónde tiene sentido."
          subtitle="Algunos ámbitos donde nuestra sensórica encaja bien. Si tu necesidad no aparece aquí, consúltanos igualmente."
        />
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {apps.map((a) => (
            <div
              key={a.title}
              className="rounded-2xl p-6 transition-colors border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 hover:border-[#0A55C2]/40 dark:hover:border-[#5B9BFF]/40"
            >
              <div className="h-10 w-10 rounded-lg bg-[#0A55C2]/10 dark:bg-slate-800 grid place-items-center">
                <a.icon className="h-4 w-4 text-[#0A55C2] dark:text-[#5B9BFF]" />
              </div>
              <h3 className="mt-5 font-semibold tracking-tight text-slate-900 dark:text-white leading-snug">
                {a.title}
              </h3>
              <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{a.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* Wind icon (lucide doesn't expose all in this version) */
function Wind(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2" />
      <path d="M9.6 4.6A2 2 0 1 1 11 8H2" />
      <path d="M12.6 19.4A2 2 0 1 0 14 16H2" />
    </svg>
  );
}

/* ----------------------- CONTACT ----------------------- */
function ContactSection() {
  return (
    <section
      id="contacto"
      className="py-20 sm:py-24 bg-white dark:bg-slate-900/30 border-b border-slate-200/60 dark:border-slate-800/80"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionHeader
            eyebrow="Contacto"
            title="¿Necesitas medir algo concreto?"
            subtitle="Cuéntanos qué necesitas y te diremos cómo hacerlo. Desarrollamos soluciones adaptadas a cada caso, sin compromiso."
            align="left"
          />
          <div className="mt-7 space-y-4">
            <a
              href={`mailto:${CONTACT_EMAIL}?subject=Consulta%20Olimpia%20Sensor`}
              className="flex items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 hover:bg-white dark:hover:bg-slate-900 hover:border-[#0A55C2]/40 transition-colors px-4 py-3 text-sm group"
              data-testid="contact-mailto"
            >
              <div className="h-9 w-9 rounded-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 grid place-items-center">
                <Mail className="h-4 w-4 text-[#0A55C2] dark:text-[#5B9BFF]" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500 dark:text-slate-400">
                  Email directo
                </div>
                <div className="font-mono text-slate-900 dark:text-slate-100">{CONTACT_EMAIL}</div>
              </div>
              <ArrowRight className="h-4 w-4 ml-auto text-slate-400 group-hover:text-[#0A55C2] dark:group-hover:text-[#5B9BFF] group-hover:translate-x-0.5 transition-all" />
            </a>
            <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 px-4 py-3 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              <ShieldCheck className="h-3.5 w-3.5 inline-block mr-1 -mt-0.5 text-emerald-600 dark:text-emerald-400" />
              Consulta sin compromiso. Te respondemos por email con propuesta o preguntas concretas
              para entender mejor tu caso.
            </div>
          </div>
        </div>
        <div className="lg:col-span-7">
          <ContactForm />
        </div>
      </div>
    </section>
  );
}

function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    measurement: "",
    conditions: "",
    website: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.measurement) {
      toast.error("Por favor rellena nombre, email y qué necesitas medir.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Mensaje enviado. Te responderemos pronto.");
      setForm({ name: "", email: "", company: "", measurement: "", conditions: "", website: "" });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo enviar el mensaje.");
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <div
        className="rounded-2xl border border-emerald-200 dark:border-emerald-700/40 bg-emerald-50/60 dark:bg-emerald-500/10 p-10 text-center"
        data-testid="contact-success"
      >
        <div className="h-12 w-12 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-500/20 grid place-items-center">
          <CheckCircle2 className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
        </div>
        <h3 className="mt-5 text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
          Mensaje recibido
        </h3>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 max-w-md mx-auto">
          Hemos recibido tu consulta. Te responderemos por email en breve. Si quieres adelantarnos
          algún detalle adicional, escríbenos a{" "}
          <a className="text-[#0A55C2] dark:text-[#5B9BFF] underline" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>
          .
        </p>
        <Button
          variant="outline"
          className="mt-6 rounded-full"
          onClick={() => setSent(false)}
          data-testid="contact-send-another"
        >
          Enviar otra consulta
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-6 sm:p-8 shadow-sm"
      data-testid="contact-form"
      noValidate
    >
      <div className="hidden" aria-hidden>
        <Label htmlFor="website">Website</Label>
        <Input
          id="website"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={(e) => update("website", e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="name" className="text-[11px] tracking-[0.18em] uppercase text-slate-500 dark:text-slate-400">
            Nombre *
          </Label>
          <Input
            id="name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Tu nombre"
            required
            data-testid="contact-name"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-[11px] tracking-[0.18em] uppercase text-slate-500 dark:text-slate-400">
            Email *
          </Label>
          <Input
            id="email"
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="tu@email.com"
            required
            data-testid="contact-email"
          />
        </div>
        <div className="sm:col-span-2 space-y-1.5">
          <Label htmlFor="company" className="text-[11px] tracking-[0.18em] uppercase text-slate-500 dark:text-slate-400">
            Empresa{" "}
            <span className="text-slate-400 dark:text-slate-500 normal-case tracking-normal">(opcional)</span>
          </Label>
          <Input
            id="company"
            value={form.company}
            onChange={(e) => update("company", e.target.value)}
            placeholder="Empresa o proyecto"
            data-testid="contact-company"
          />
        </div>
        <div className="sm:col-span-2 space-y-1.5">
          <Label htmlFor="measurement" className="text-[11px] tracking-[0.18em] uppercase text-slate-500 dark:text-slate-400">
            ¿Qué necesitas medir? *
          </Label>
          <Textarea
            id="measurement"
            rows={3}
            value={form.measurement}
            onChange={(e) => update("measurement", e.target.value)}
            placeholder="Por ejemplo: temperatura del suelo en 4 puntos de un invernadero, sin red WiFi disponible…"
            required
            data-testid="contact-measurement"
          />
        </div>
        <div className="sm:col-span-2 space-y-1.5">
          <Label htmlFor="conditions" className="text-[11px] tracking-[0.18em] uppercase text-slate-500 dark:text-slate-400">
            Condiciones del proyecto{" "}
            <span className="text-slate-400 dark:text-slate-500 normal-case tracking-normal">(opcional)</span>
          </Label>
          <Textarea
            id="conditions"
            rows={3}
            value={form.conditions}
            onChange={(e) => update("conditions", e.target.value)}
            placeholder="Ubicación, alimentación disponible (red, batería, solar), si necesitas acceso web a los datos…"
            data-testid="contact-conditions"
          />
        </div>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-[11px] text-slate-500 dark:text-slate-400">
          Al enviar este formulario aceptas que te respondamos a tu email. No compartimos tus datos.
        </p>
        <Button
          type="submit"
          disabled={submitting}
          className="rounded-full bg-[#0A55C2] hover:bg-[#0848a8] text-white px-6 h-11"
          data-testid="contact-submit"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enviando…
            </>
          ) : (
            <>
              Enviar consulta
              <Send className="h-4 w-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </form>
  );
}

/* ----------------------- FOOTER ----------------------- */
function Footer() {
  return (
    <footer className="py-10 bg-slate-50 dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-slate-500 dark:text-slate-400">
        <div className="flex items-center gap-3">
          <img
            src="/brand/logo.png"
            alt=""
            className="h-8 w-8 object-contain dark:[filter:brightness(0)_invert(1)]"
            draggable="false"
          />
          <div>
            <div className="font-semibold text-slate-700 dark:text-slate-200">Olimpia Sensor</div>
            <div>Sensórica y monitorización a medida</div>
          </div>
        </div>
        <div className="flex items-center gap-5">
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="hover:text-slate-900 dark:hover:text-white transition-colors"
          >
            {CONTACT_EMAIL}
          </a>
          <Link
            to="/login"
            className="hover:text-slate-900 dark:hover:text-white transition-colors"
          >
            Acceso clientes
          </Link>
          <span>© {new Date().getFullYear()} Olimpia Sensor</span>
        </div>
      </div>
    </footer>
  );
}

/* ----------------------- HELPERS ----------------------- */
function SectionHeader({ eyebrow, title, subtitle, align = "center" }) {
  const alignCls = align === "left" ? "text-left" : "text-center mx-auto";
  return (
    <div className={`max-w-2xl ${alignCls}`}>
      <p className="text-[11px] tracking-[0.22em] uppercase text-[#0A55C2] dark:text-[#5B9BFF] font-medium">
        {eyebrow}
      </p>
      <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white leading-[1.15]">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-4 text-base text-slate-600 dark:text-slate-300 leading-relaxed">{subtitle}</p>
      )}
    </div>
  );
}
